<script setup lang="ts">
import {Icon} from '@iconify/vue'
import {storeToRefs} from 'pinia'
import {onMounted, onUnmounted, ref} from 'vue'

import type {BellowsDirection} from '@/components/harmonika'
import {BassLayoutEditor, buttonNoteLabel, InstrumentBody} from '@/components/harmonika'
import InstrumentTuningSelect from '@/components/harmonika/InstrumentTuningSelect.vue'
import {useSettingsStore} from '@/stores/settings'

import ControlsBar from './ControlsBar.vue'
import LegendBar from './LegendBar.vue'
import SettingsModal from './SettingsModal.vue'
import {useHarmonikaAudio} from './useHarmonikaAudio'
import {useKeyMappings} from './useKeyMappings'

// Unter-Tabs des Harmonika-Tabs als Routen (/harmonika, /harmonika/bass – router/routes.ts).
// „Spielen" ist der Simulator, „Bass-Belegung" der Editor der Begleitseite – die Tastatur-Handler
// unten greifen nur im Simulator (siehe `handleKeyDown`).
const {view = 'play'} = defineProps<{ view?: 'play' | 'bass' }>()

const showSettings = ref(false)
const SUB_TABS = [
  {id: 'play', route: 'keyboard', label: 'Spielen'},
  {id: 'bass', route: 'keyboard-bass', label: 'Bass-Belegung'},
] as const

const {
  keyMappings,
  layoutMap,
  updateMapping,
  getKeyLabel,
  findButtonByKeyCode,
  resetToDefaults,
} = useKeyMappings()

// Stimmung meiner Harmonika: verschiebt Beschriftung und Klang, nicht die Knopftabelle.
const {instrumentTuningId, instrumentTuningOffset} = storeToRefs(useSettingsStore())

const {
  direction,
  activeButtons,
  activeMidiNotes,
  activateButton,
  deactivateButton,
} = useHarmonikaAudio()

function getButtonLabel(rowIdx: number, btnIdx: number) {
  return buttonNoteLabel(rowIdx, btnIdx, direction.value, instrumentTuningOffset.value)
}

function setDirection(nextDirection: BellowsDirection) {
  direction.value = nextDirection
}

function handleKeyDown(event: KeyboardEvent) {
  if (event.repeat) return
  if (view !== 'play') return

  if (event.code === 'Space') {
    event.preventDefault()
    setDirection('ZUG')
    return
  }

  if (showSettings.value) return

  const buttonPosition = findButtonByKeyCode(event.code)
  if (!buttonPosition) return

  event.preventDefault()
  const [rowIdx, btnIdx] = buttonPosition
  activateButton(rowIdx, btnIdx)
}

function handleKeyUp(event: KeyboardEvent) {
  if (view !== 'play') return
  if (event.code === 'Space') {
    event.preventDefault()
    setDirection('DRUCK')
    return
  }

  const buttonPosition = findButtonByKeyCode(event.code)
  if (!buttonPosition) return

  event.preventDefault()
  const [rowIdx, btnIdx] = buttonPosition
  deactivateButton(rowIdx, btnIdx)
}

onMounted(() => {
  window.addEventListener('keydown', handleKeyDown)
  window.addEventListener('keyup', handleKeyUp)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown)
  window.removeEventListener('keyup', handleKeyUp)
})
</script>

<template>
  <div class="flex   text-primary min-h-[70dvh] flex-1 flex-col items-center-safe justify-center-safe overflow-x-auto rounded-2xlbg-gradient-to-r from-sky-200 to-cyan-100 p-4 font-sans  select-none">
    <SettingsModal
        v-if="showSettings"
        :key-mappings="keyMappings"
        :layout-map="layoutMap"
        @mapping-change="updateMapping"
        @reset-to-defaults="resetToDefaults"
        @close="showSettings = false"
    />

    <div class="flex w-full max-w-6xl flex-col items-center gap-6">
      <!-- Titel-Banner (früher separate HeaderBar der Standalone-App) -->
      <header class="flex w-full items-end justify-between px-4">
        <div class="text-left">
          <h1 class="font-serif text-3xl font-bold tracking-wider text-amber-500 md:text-4xl">
            Steirische
          </h1>
          <p class="text-xs  md:text-sm">
            Web Audio Edition
          </p>
        </div>

        <div class="flex items-center gap-2">
          <label class="flex items-center gap-2 text-sm ">
            <span class="hidden sm:inline">Stimmung</span>
            <InstrumentTuningSelect
                v-model:tuning="instrumentTuningId"
                class="border-gray-500/80 bg-gradient-to-r from-sky-50/20 to-cyan-50/30 "
            />
          </label>
          <button
              type="button"
              class="flex items-center gap-2 rounded-lg border border-gray-500/80 bg-gradient-to-r from-sky-50/20 to-cyan-50/30 px-4 py-2 text-sm text-primary-500 transition-colors hover:bg-stone-700"
              @click="showSettings = true"
          >
            <Icon icon="lucide:settings" class="text-lg"/>
            <span class="hidden sm:inline">Tasten-Layout</span>
          </button>
        </div>
      </header>

      <nav class="flex w-full gap-1 px-4" aria-label="Harmonika-Ansicht">
        <RouterLink
            v-for="tab in SUB_TABS"
            :key="tab.id"
            :to="{name: tab.route}"
            class="rounded-t-lg px-4 py-2 text-sm transition-colors"
            :class="view === tab.id
            ? 'bg-gray-200 font-semibold text-sky-500'
            : 'bg-gray-100 text-sky-400 hover:bg-gray-200/20 hover:text-stone-800'"
            :aria-current="view === tab.id ? 'page' : undefined"
        >
          {{ tab.label }}
        </RouterLink>
      </nav>

      <template v-if="view === 'play'">

        <ControlsBar v-model:direction="direction"/>

        <!-- playable aus: der Simulator spielt selbst (useHarmonikaAudio), inkl. Balgwechsel bei gehaltenem Knopf. -->
        <InstrumentBody
            variant="simulator"
            :playable="false"
            :active-buttons="activeButtons"
            :active-midi-notes="activeMidiNotes"
            :get-label="getButtonLabel"
            :get-key-label="getKeyLabel"
            @activate="activateButton"
            @deactivate="deactivateButton"
        />

        <LegendBar :active-buttons-count="activeButtons.size"/>
      </template>

      <section v-else class="w-full rounded-xl bg-stone-100 p-4 text-stone-800">
        <BassLayoutEditor/>
      </section>
    </div>
  </div>
</template>
